/**
 * Vite SPA entry. Mounts React, the TanStack Router, and the
 * persisted TanStack Query client.
 *
 * Migration note: this is the equivalent of Next.js's
 * frontend/src/app/layout.tsx providers stack, but for a pure SPA —
 * no server, no hydration, no async boundaries.
 */

import "@/styles/globals.css";

import { StrictMode } from "react";
import { createRoot } from "react-dom/client";
import { RouterProvider, createRouter } from "@tanstack/react-router";
import {
  PersistQueryClientProvider,
} from "@tanstack/react-query-persist-client";
import { Toaster } from "sonner";

import { queryClient, persister } from "@/lib/query-client";
import { routeTree } from "@/routeTree.gen";
import { AuthProvider } from "@/lib/auth";
import { RouteError } from "@/components/shared/route-error";

const router = createRouter({
  routeTree,
  defaultPreload: "intent",
  // Preserve route loaders' freshness; matches TanStack Query's stale
  // gating so an explicit nav re-uses cached data without refetch.
  defaultPreloadStaleTime: 0,
  // Global error fallback (2026-06-04). Covers all 140 authenticated
  // routes + auth flow + marketing in one line — without it, a
  // render-time throw lands on TanStack's built-in DefaultErrorComponent
  // which displays the raw stack trace. Per-route overrides can pass
  // their own `errorComponent` for module-specific copy.
  defaultErrorComponent: RouteError,
  context: {
    // QueryClient is exposed to route loaders so they can prefetch on
    // hover without re-wiring per-route.
    queryClient,
  },
});

declare module "@tanstack/react-router" {
  interface Register {
    router: typeof router;
  }
}

const rootEl = document.getElementById("root");
if (!rootEl) throw new Error("missing #root — index.html is malformed");

createRoot(rootEl).render(
  <StrictMode>
    <PersistQueryClientProvider
      client={queryClient}
      persistOptions={{
        persister: persister!,
        // Discard persisted queries older than 24h to avoid serving
        // stale per-user data after a long absence.
        maxAge: 24 * 60 * 60 * 1000,
      }}
    >
      {/* AuthProvider runs the mount-time /me probe and exposes user /
          tenant / features / login / logout to every route via
          useAuth(). Sits inside PersistQueryClientProvider because
          authApi calls and downstream module queries all share the same
          QueryClient. */}
      <AuthProvider>
        <RouterProvider router={router} />
      </AuthProvider>
      <Toaster richColors closeButton position="top-right" />
    </PersistQueryClientProvider>
  </StrictMode>,
);
